import {
    Button,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    FileUpload,
    FileUploadDropzone,
    FileUploadItem,
    FileUploadItemDelete,
    FileUploadItemMetadata,
    FileUploadItemPreview,
    FileUploadList,
    FileUploadTrigger,
} from '@repo/shadcn';
import { Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FaFileUpload } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ExcelIcon } from '@/assets/svg.icons/icons';
import { Route, SLICE_TAG } from '@/constants';
import {
    useImportProductsCsvMutation,
    useLazyExportProductsCsvQuery,
} from '@/features/slice/products/product/productApiSlice';
import { logError } from '@/utils';

import { useInvalidateTag } from '../../../../hooks';

export const ProductListHeadingActions = () => {
    const [downloadProductsCsv, { isLoading }] =
        useLazyExportProductsCsvQuery();

    const handleExportCsv = async () => {
        try {
            // toast.
            await downloadProductsCsv({
                limit: 100000000,
            }).unwrap();
            toast.success('CSV downloaded successfully!', {});
        } catch (error) {
            logError('ProductListHeadingActions', error);
            toast.error('Error downloading CSV. Please try again.');
        }
    };

    return (
        <div className="flex gap-4">
            <ProductImportModal />
            <Button variant="ghost" className="" onClick={handleExportCsv}>
                {isLoading ? 'Exporting...' : <ExcelIcon />}
            </Button>

            <div>
                <Button type={'button'}>
                    <Link to={Route.PRODUCT_CREATE_NEW}>
                        Create New Product
                    </Link>
                </Button>
            </div>
        </div>
    );
};

const ProductImportModal = () => {
    const [importProductsCsv, { isLoading }] = useImportProductsCsvMutation();
    const { invalidateTag } = useInvalidateTag();

    const [files, setFiles] = useState<File[]>([]);

    const handleImportCsv = async () => {
        if (files.length === 0) {
            toast.error('Please select a CSV file to import.');
            return;
        }

        const toastId = toast.loading('Importing products...');
        try {
            await importProductsCsv(files[0]).unwrap();
            toast.success('Products imported successfully!');
            setFiles([]);
            invalidateTag(SLICE_TAG.PRODUCTS_V2);
        } catch (error) {
            console.error('Import failed', error);
            toast.error('Import failed. Please try again.');
            return;
        } finally {
            toast.dismiss(toastId);
        }
    };

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="ghost" className="">
                    <FaFileUpload />
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Import Products</DialogTitle>
                    <DialogDescription>
                        Accepted file format: .csv
                    </DialogDescription>
                </DialogHeader>
                <FileUploader files={files} setFiles={setFiles} />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="button"
                        disabled={files.length <= 0 || isLoading}
                        onClick={handleImportCsv}
                    >
                        {isLoading ? 'Importing...' : 'Import'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type FileUploaderProps = {
    files: File[];
    setFiles: (files: File[]) => void;
};

export function FileUploader({ files, setFiles }: FileUploaderProps) {
    const onFileReject = useCallback((file: File, message: string) => {
        toast(message, {
            description: `"${file.name.length > 20 ? `${file.name.slice(0, 20)}...` : file.name}" has been rejected`,
        });
    }, []);

    return (
        <FileUpload
            maxFiles={1}
            maxSize={50 * 1024 * 1024}
            className="w-full max-w-md"
            value={files}
            onValueChange={setFiles}
            onFileReject={onFileReject}
        >
            <FileUploadDropzone>
                <div className="flex flex-col items-center gap-1 text-center">
                    <div className="flex items-center justify-center rounded-full border p-2.5">
                        <Upload className="size-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium">
                        Drag & drop files here
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Or click to browse (max 1 file, up to 50MB)
                    </p>
                </div>
                <FileUploadTrigger asChild>
                    <Button variant="outline" size="sm" className="mt-2 w-fit">
                        Browse files
                    </Button>
                </FileUploadTrigger>
            </FileUploadDropzone>
            <FileUploadList>
                {files.map((file, index) => (
                    <FileUploadItem key={index} value={file}>
                        <FileUploadItemPreview />
                        <FileUploadItemMetadata />
                        <FileUploadItemDelete asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7"
                            >
                                <X />
                            </Button>
                        </FileUploadItemDelete>
                    </FileUploadItem>
                ))}
            </FileUploadList>
        </FileUpload>
    );
}
